import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button, Select } from '@chakra-ui/react';
import DisplayAmount from "../../shared/DisplayAmount";

interface ProviderAccount {
    label: string;
    value: string;
}

interface Provider {
    id: string;
    name: string;
    provider_name: string;
    provider_account: ProviderAccount[];
    method: any[];  // Update this type according to your actual method structure
}

interface ProviderMethod {
    label: string;
    value: string;
}

const BatchAssignProviderModal: React.FC<{
    batchToAssignProvider: any;
    batchProviderList: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, batchToAssignProvider, batchProviderList, setSwalProps }) => {
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [providerAccount, setProviderAccount] = useState<string>("");
    const [method, setMethod] = useState<string>("");
    const [providerAccountList, setProviderAccountList] = useState<any>([]);
    const [methodList, setMethodList] = useState<any>([]);
    const [providerAccountMethodMap, setProviderAccountMethodMap] = useState<any>([]);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    const isFormValid = providerAccount !== "" && method !== "" ? true : false;

    const closeModal = async () => {
        setProviderAccount("");
        setMethod("");
        setMethodList([]);
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const providerName = getProviderNameByAccountId(providerAccount);
            const providerAccountName = getProviderAccountNameById(providerAccount);
            const methodName = getMethodNameByMethodId(method);

            const postData = {
                batch_id: batchToAssignProvider.id,
                provider_account_id: providerAccount,
                method_id: method,
                provider: providerName,
                provider_account: providerAccountName,
                method: methodName,
                username: localStorage.getItem("username"),
                userRole: userRole,
                subaccount_id: batchToAssignProvider.subaccount.id
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole}/batch/action/batch-assign-provider`, postData)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        onClose();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "The provider has been assigned",
                            showConfirmButton: true,
                            didClose: () => {
                                setProviderAccount("");
                                setMethod("");
                                triggerReload();
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const handleProviderDropdownChange = async (event: any) => {
        setMethodList([]);
        setMethod("");

        const { value } = event.target;
        setProviderAccount(value);
        if (value !== "") {
            const getProviderAccountMethod = providerAccountMethodMap.find((x: any) => x.account_id === value);
            setMethodList(getProviderAccountMethod.methods);
        }
    };

    const handleProviderMethodDropdownChange = async (event: any) => {
        const { value } = event.target;
        setMethod(value);
    };

    function getProviderNameByAccountId(accountId: string): string | undefined {
        const provider = batchProviderList.find((provider: Provider) =>
            provider.provider_account.some((account: any) => account.id === accountId)
        );
        return provider?.name;
    }

    function getProviderAccountNameById(providerAccountId: string): string | undefined {
        const provider = batchProviderList.find((provider: any) =>
            provider.provider_account.some((account: any) => account.id === providerAccountId)
        );

        const account = provider?.provider_account.find((account: any) => account.id === providerAccountId);

        return account?.account_name;
    }

    function getMethodNameByMethodId(methodId: string): string | undefined {
        const provider = batchProviderList.find((provider: Provider) =>
            provider.method.some((method: any) => method.id === methodId)
        );
        const method = provider?.method.find((method: any) => method.id === methodId);
        return method?.classfile;
    }

    const buildUpProviderAndMethodList = (batchProviderList: any) => {
        const providerAccountList: ProviderAccount[] = [];
        const providerAccountMethodMap: { account_id: string; methods: ProviderMethod[] }[] = [];

        if (batchProviderList && Array.isArray(batchProviderList)) {
            batchProviderList.forEach((provider: any) => {
                if (provider?.provider_account && Array.isArray(provider.provider_account)) {
                    provider.provider_account.forEach((account: any) => {
                        if (account?.id && account?.account_name) {
                            providerAccountList.push({
                                label: `${provider.name} (${account.account_name})`,
                                value: account.id,
                            });

                            // Collect methods associated with this account's provider
                            const methodsForAccount = provider.method?.map((method: any) => ({
                                label: method.name,
                                value: method.id
                            })) || [];

                            providerAccountMethodMap.push({
                                account_id: account.id,
                                methods: methodsForAccount,
                            });
                        }
                    });
                }
            });

            setProviderAccountList(providerAccountList);
            setProviderAccountMethodMap(providerAccountMethodMap);
        }

        return batchProviderList;
    };

    useEffect(() => {
        buildUpProviderAndMethodList(batchProviderList);
    }, [batchProviderList]);

    return (
        <>
            {batchToAssignProvider &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-5 w-5"
                                        fill="currentColor"
                                    >
                                        <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.44.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Assign Provider To Batch
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        Configure provider settings for this batch
                                    </p>
                                </div>
                            </div>

                            {batchToAssignProvider.is_manual !== undefined && batchToAssignProvider.is_manual !== null && <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
                                You can assign provider until the batch status reaches <span className="font-semibold">{batchToAssignProvider.is_manual === "true" ? "QUEUED" : "IN PROGRESS"}</span>. Assigning a new provider will replace the current one.
                            </div>}

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <div className="flex flex-col gap-3 text-sm">
                                    {userRole === 'superadmin' && batchToAssignProvider.provider_name && batchToAssignProvider.provider_account_name && batchToAssignProvider.provider_method_name &&
                                        <>
                                            <div className="flex">
                                                <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                    Current Provider
                                                </div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {batchToAssignProvider.provider_name} ({batchToAssignProvider.provider_account_name})
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                    Current Method
                                                </div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {batchToAssignProvider.provider_method_name}
                                                </div>
                                            </div>
                                        </>
                                    }

                                    {userRole !== 'superadmin' &&
                                        <div className="flex">
                                            <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                Reference
                                            </div>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                                {batchToAssignProvider.batch_reference}
                                            </div>
                                        </div>
                                    }

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Record(s)
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {batchToAssignProvider.record}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Amount
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {batchToAssignProvider.crypto ? batchToAssignProvider.amount : <DisplayAmount amount={Number(batchToAssignProvider.amount)} currency={batchToAssignProvider.asset} />} {batchToAssignProvider.asset}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Provider <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    name="provider"
                                    isDisabled={isProcessing}
                                    id="provider"
                                    value={providerAccount}
                                    onChange={handleProviderDropdownChange}
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                >
                                    <option value="">Please Select</option>
                                    {providerAccountList.map((providerAccount: any) => (
                                        <option key={providerAccount.value} value={providerAccount.value}>
                                            {providerAccount.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>

                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Provider Method <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    name="method"
                                    isDisabled={isProcessing}
                                    id="method"
                                    value={method}
                                    onChange={handleProviderMethodDropdownChange}
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                >
                                    <option value="">Please Select</option>
                                    {methodList.map((method: any) => (
                                        <option key={method.value} value={method.value}>
                                            {method.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Not now
                                </Button>

                                <Button
                                    onClick={() => handleSubmit()}
                                    isDisabled={!isFormValid || isProcessing}
                                    isLoading={isProcessing}
                                    loadingText='Processing...'
                                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Assign provider
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default BatchAssignProviderModal;